import { useState } from "react";
import {
  useFloating,
  offset,
  flip,
  shift,
  autoUpdate,
  FloatingPortal,
  useDismiss,
  useInteractions,
} from "@floating-ui/react";
import { buildClasses } from "../utils/buildClasses";
import { TAG_COLORS } from "./tagColors";
import { TagDot } from "./TagDot";

export function TagColorPicker({
  currentColorId,
  onSelect,
}: {
  currentColorId: string;
  onSelect: (colorId: string) => void;
}): JSX.Element {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [offset(4), flip(), shift()],
  });
  const dismiss = useDismiss(context);
  const { getFloatingProps } = useInteractions([dismiss]);

  return (
    <>
      <button
        ref={refs.setReference}
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-label="Change color"
        className={buildClasses(
          "flex",
          "items-center",
          "justify-center",
          "w-6",
          "h-6",
          "rounded-full",
          "hover:bg-on-surface-100",
        )}
      >
        <TagDot colorId={currentColorId} size="md" />
      </button>
      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className={buildClasses(
              "z-[2000000000]",
              "bg-surface-50",
              "border-tiny",
              "border-on-surface-200",
              "rounded-md",
              "shadow-lg",
              "p-2",
            )}
          >
            <div
              className={buildClasses(
                "grid",
                "grid-cols-5",
                "gap-1.5",
                "w-[10rem]",
              )}
            >
              {TAG_COLORS.map((color) => {
                const isSelected = color.id === currentColorId;
                return (
                  <button
                    key={color.id}
                    type="button"
                    aria-label={color.id}
                    onClick={() => {
                      onSelect(color.id);
                      setIsOpen(false);
                    }}
                    className={buildClasses(
                      "w-6",
                      "h-6",
                      "rounded-full",
                      "flex",
                      "items-center",
                      "justify-center",
                      "border-2",
                      {
                        if: isSelected,
                        then: "border-on-surface-800",
                        else: buildClasses(
                          "border-transparent",
                          "hover:border-on-surface-300",
                        ),
                      },
                    )}
                  >
                    <span
                      className={buildClasses(
                        "w-4",
                        "h-4",
                        "rounded-full",
                      )}
                      style={{ backgroundColor: color.dot }}
                    />
                  </button>
                );
              })}
            </div>
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
